import type { Cohort, Invite2, MentorLoad, Person2 } from "@/app/peopleQueries";
import { plural } from "@/lib/plural";

/** Чистые правила A2: всё считается из строк, тексты — из макета. */

export type Role = Person2["role"];
export type RoleFilter = "all" | Role;
export type Scope = "all" | "nocohort" | string;

/** Основная цепочка маршрута — 29 шагов (CLAUDE.md); «Алгоритмы» не считаются. */
export const MAIN_STEPS = 29;

export const ROLE_RU: Record<Role, string> = { student: "студент", mentor: "ментор", admin: "админ", owner: "владелец" };

/** Фильтры ролей сайдбара — владелец не выбирается (макет), но попадает в «все». */
export const ROLE_FILTERS: readonly { id: RoleFilter; label: string }[] = [
  { id: "all", label: "все" },
  { id: "student", label: "студент" },
  { id: "mentor", label: "ментор" },
  { id: "admin", label: "админ" },
];

/** Права ролей — дословно из макета. */
export const PERMS: readonly { role: string; tone: "ink2" | "acc" | "info" | "mut"; can: string }[] = [
  { role: "студент", tone: "ink2", can: "Видит свой маршрут, сдаёт домашки, читает ревью" },
  { role: "ментор", tone: "acc", can: "Проверяет сдачи своих студентов, пишет заметки, видит когорту" },
  { role: "админ", tone: "info", can: "Правит курс и материалы, управляет людьми и когортами" },
  { role: "владелец", tone: "mut", can: "Всё перечисленное плюс биллинг и удаление когорт" },
];

export const INVITE_NOTE = "Приглашение уходит письмом и ссылкой в Telegram. Пока человек не примет, он висит в списке как «ожидает».";
export const NO_COHORT_META = "ведутся индивидуально, без общего старта";

export const people = (n: number) => `${n} ${plural(n, "человек", "человека", "человек")}`;
export const students = (n: number) => `${n} ${plural(n, "студент", "студента", "студентов")}`;

export function roleCounts(rows: Person2[]): Record<RoleFilter, number> {
  const c: Record<RoleFilter, number> = { all: rows.length, student: 0, mentor: 0, admin: 0, owner: 0 };
  for (const r of rows) c[r.role]++;
  return c;
}

/** Фильтр ментора: "" — все, "none" — без ментора, иначе id. */
export type MentorFilter = "" | "none" | string;

export function filterRows(rows: Person2[], role: RoleFilter, mentor: MentorFilter, query: string): Person2[] {
  const q = query.trim().toLowerCase();
  return rows.filter((r) => {
    if (role !== "all" && r.role !== role) return false;
    if (mentor === "none" && (r.mentor_id || r.role !== "student")) return false;
    if (mentor && mentor !== "none" && r.mentor_id !== mentor) return false;
    if (q && !`${r.name} ${r.email} ${cohortLabel(r)}`.toLowerCase().includes(q)) return false;
    return true;
  });
}

export const isMentor = (p: Person2) => p.role === "mentor";

/** Когорта в строке: у студента без когорты — «без когорты», у персонала — «—». */
export function cohortLabel(p: Person2): string {
  return p.cohort || (p.role === "student" ? "без когорты" : "—");
}

/** Подзаголовок таблицы — из строк текущего режима. */
export function subtitle(scope: Scope, rows: Person2[]): string {
  const n = rows.length;
  if (scope === "all") return `${people(n)} · во всех когортах и вне их`;
  if (scope === "nocohort") return `${people(n)} · ${NO_COHORT_META}`;
  const c = roleCounts(rows);
  const parts = [people(n), students(c.student)];
  if (c.mentor) parts.push(`${c.mentor} ${plural(c.mentor, "ментор", "ментора", "менторов")}`);
  if (c.admin + c.owner) parts.push(`${c.admin + c.owner} ${plural(c.admin + c.owner, "админ", "админа", "админов")}`);
  return parts.join(" · ");
}

export function scopeTitle(scope: Scope, cohorts: Cohort[]): string {
  if (scope === "all") return "Все студенты";
  if (scope === "nocohort") return "Без когорты";
  return cohorts.find((c) => c.id === scope)?.name ?? "Когорта";
}

/** Подпись карточки когорты: «N-я неделя» · «завершена» · «набор». */
export function cohortMeta(c: Cohort): { label: string; tone: "acc" | "mut" | "warn" } {
  if (c.status === "finished") return { label: `завершена${c.starts_on ? ` · старт ${shortDate(c.starts_on)}` : ""}`, tone: "mut" };
  if (c.status === "recruiting") return { label: `набор открыт${c.starts_on ? ` · старт ${shortDate(c.starts_on)}` : ""}`, tone: "warn" };
  if (c.week <= 0) return { label: c.starts_on ? `старт ${shortDate(c.starts_on)}` : "ещё не началась", tone: "acc" };
  return { label: `${c.starts_on ? `старт ${shortDate(c.starts_on)} · ` : ""}${c.week}-я неделя`, tone: "acc" };
}

export function shortDate(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-");
  return y && m && d ? `${d}.${m}` : iso;
}

export function gatesLabel(p: Person2): string {
  return p.role === "student" ? `${p.gates_passed} из ${MAIN_STEPS}` : "—";
}

export const STATUS_TONE: Record<Person2["status"], "ok" | "warn" | "mut"> = { активен: "ok", молчит: "warn", ожидает: "mut" };

/** Состояние приглашения: ожидает ≤ 2 дней — warn, ≥ 5 — err; принято — ok. */
export function inviteState(inv: Invite2): { label: string; tone: "ok" | "warn" | "err" | "mut" } {
  if (inv.state === "принято") return { label: "принято", tone: "ok" };
  if (inv.state === "истекло") return { label: "истекло", tone: "mut" };
  const d = inv.days;
  const label = d <= 0 ? "ожидает · сегодня" : `ожидает ${d} ${plural(d, "день", "дня", "дней")}`;
  return { label, tone: d >= 5 ? "err" : "warn" };
}

/** Ширина полосы нагрузки: n / max. */
export function loadWidth(n: number, max: number): number {
  return max > 0 ? Math.round((n / max) * 100) : 0;
}

export function loadTone(load: MentorLoad, n: number): "warn" | "acc" {
  return load.skewed && n === load.max && n > 0 ? "warn" : "acc";
}

/** Нижняя медиана sorted[floor(n/2)] — единое определение A2/M4/A3. */
export function medianGates(rows: Person2[]): number | null {
  const xs = rows.filter((r) => r.role === "student").map((r) => r.gates_passed).sort((a, b) => a - b);
  if (xs.length === 0) return null;
  return xs[Math.floor(xs.length / 2)] ?? null;
}

/** Текст пачки: сколько выбрано и скольким из них можно назначить ментора. */
export function bulkLabel(selected: Person2[]): { title: string; hint?: string } {
  const n = selected.length;
  const st = selected.filter((p) => p.role === "student").length;
  const title = `выбрано ${people(n)}`;
  if (st === n) return { title };
  if (st === 0) return { title, hint: "ментор назначается только студентам — среди выбранных их нет" };
  return { title, hint: `ментор назначается только студентам — ${students(st)} из ${n}` };
}

/** Отчёт после отправки приглашений. */
export function inviteReport(created: number, skipped: string[]): string {
  const parts = [`отправлено ${created} ${plural(created, "приглашение", "приглашения", "приглашений")}`];
  if (skipped.length) parts.push(`пропущено ${skipped.length}`);
  return parts.join(" · ");
}
